// src/components/partnerships/PartnershipsHero.js
import React, { useEffect } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";

// ✅ Counter animation component
const Counter = ({ from = 0, to, suffix = "", decimals = 0, duration = 2 }) => {
  const count = useMotionValue(from);
  const rounded = useTransform(count, (latest) =>
    suffix === "%"
      ? `${Math.round(latest)}%`
      : latest >= 1000 && suffix !== "%"
      ? `${Intl.NumberFormat("en-US", {
          notation: "compact",
          maximumFractionDigits: decimals,
        }).format(latest)}${suffix}`
      : `${latest.toFixed(decimals)}${suffix}`
  );

  useEffect(() => {
    const controls = animate(count, to, { duration, ease: "easeOut" });
    return controls.stop;
  }, [count, to, duration]);

  return <motion.span>{rounded}</motion.span>;
};

const PartnershipsHero = () => {
  return (
    <section className="relative bg-gradient-to-b from-black via-[#050E24] to-[#0A2A66] text-white py-32 md:py-40 overflow-hidden">
      {/* Background glow layers */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-1/4 w-[700px] h-[700px] bg-[radial-gradient(ellipse_at_center,rgba(77,178,211,0.25),transparent_70%)] blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-[radial-gradient(ellipse_at_center,rgba(61,143,170,0.15),transparent_70%)] blur-3xl animate-pulse delay-700"></div>
        <div className="absolute left-1/2 top-0 w-[2px] h-full bg-gradient-to-b from-cyan-400/40 via-transparent to-transparent opacity-40 animate-pulse"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto text-center px-6">
        {/* Heading */}
        <motion.h1
          className="text-4xl md:text-6xl font-bold mb-6 tracking-tight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          Build the Future of{" "}
          <span className="bg-gradient-to-r from-[#4DB2D3] via-[#6CD6E6] to-[#A5F5E2] bg-clip-text text-transparent animate-gradient-flow">
            Institutional DeFi
          </span>
        </motion.h1>

        {/* Subheading */}
        <motion.p
          className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-10 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          Collaborate with QuantNexus Capital to integrate your strategies,
          liquidity networks, or custody frameworks into automated,
          non-custodial vault infrastructure. Together, we connect institutional
          capital with decentralized execution.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-wrap justify-center gap-4 mt-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {/* Primary CTA */}
          <a
            href="#contact"
            className="group relative inline-flex items-center justify-center px-10 py-4 overflow-hidden font-semibold text-lg rounded-xl text-white bg-gradient-to-r from-cyan-500 via-blue-500 to-blue-700 shadow-[0_0_20px_rgba(0,200,255,0.25)] hover:shadow-[0_0_35px_rgba(0,200,255,0.4)] transition-all duration-500 hover:scale-[1.03]"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-0 group-hover:opacity-30 blur-xl transition duration-500"></span>
            <span className="relative z-10">Request Partnership Brief</span>
          </a>

          {/* Secondary CTA */}
          <a
            href="#frameworks"
            className="group relative inline-flex items-center justify-center px-10 py-4 font-medium text-lg rounded-xl text-white/90 border border-white/20 backdrop-blur-md bg-white/5 hover:bg-white/10 hover:border-cyan-400/60 transition-all duration-500 hover:scale-[1.02]"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/10 to-transparent opacity-0 group-hover:opacity-100 animate-gradient-sweep rounded-xl"></span>
            <span className="relative z-10">View Integration Framework</span>
          </a>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-10 text-center text-white/80"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
        >
          <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 transition-all">
            <p className="text-4xl font-bold text-cyan-400 mb-1">
              <Counter to={16} suffix="+" duration={2.5} />
            </p>
            <p className="text-sm uppercase tracking-wider">
              Integrated Strategies
            </p>
          </div>

          <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 transition-all">
            <p className="text-4xl font-bold text-cyan-400 mb-1">
              $<Counter to={1000000000} suffix="+" duration={2.5} />
            </p>
            <p className="text-sm uppercase tracking-wider">On-Chain Volume</p>
          </div>

          <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md hover:bg-white/10 transition-all">
            <p className="text-4xl font-bold text-cyan-400 mb-1">
              <Counter to={2} suffix="+" duration={2.5} />
            </p>
            <p className="text-sm uppercase tracking-wider">
              Strategic Partners
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnershipsHero;
